import React, { useState } from 'react';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText, SxProps } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import PriorityChipDossier from '../PriorityChipDossier/PriorityChipDossier';

export type EditablePriorityChipProps = {
    critical: boolean;
    onPriorityChange?: (newCritical: boolean) => void;
    disabled?: boolean;
    isLoading?: boolean;
    fontSx?: SxProps;
};

/**
 * Composant permettant de modifier la priorité d'un dossier via un menu déroulant.
 */
const EditablePriorityChip: React.FunctionComponent<EditablePriorityChipProps> = function ({
    critical,
    onPriorityChange,
    disabled = false,
    isLoading = false,
    fontSx,
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const open = Boolean(anchorEl);

    const isEditable = Boolean(onPriorityChange && !disabled && !isLoading);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (isEditable) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handlePrioritySelect = (newCritical: boolean) => {
        if (onPriorityChange && newCritical !== critical) {
            onPriorityChange(newCritical);
        }
        handleClose();
    };

    return (
        <>
            <Box
                onClick={handleClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    display: 'inline-flex',
                    cursor: isEditable ? 'pointer' : 'default',
                    opacity: disabled ? 0.6 : 1,
                }}
            >
                <PriorityChipDossier
                    critical={critical}
                    fontSx={fontSx}
                    showDropdownIndicator={isEditable}
                    isHovered={isHovered}
                    isLoading={isLoading}
                />
            </Box>

            {isEditable && (
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                    PaperProps={{
                        sx: {
                            minWidth: 150,
                            mt: 0.5,
                        },
                    }}
                >
                    <MenuItem
                        onClick={() => handlePrioritySelect(false)}
                        selected={!critical}
                        sx={{ py: 1 }}
                    >
                        <ListItemIcon sx={{ minWidth: '24px' }}>
                            <FaOptionIcon
                                icon={['fasl', 'circle']}
                                color="primary.main"
                                size="sm"
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary="Normal"
                            primaryTypographyProps={{ fontSize: '14px' }}
                        />
                    </MenuItem>

                    <MenuItem
                        onClick={() => handlePrioritySelect(true)}
                        selected={critical}
                        sx={{ py: 1 }}
                    >
                        <ListItemIcon sx={{ minWidth: '24px' }}>
                            <FaOptionIcon
                                icon={['fasl', 'exclamation-triangle']}
                                color="error.main"
                                size="sm"
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary="Urgent"
                            primaryTypographyProps={{ fontSize: '14px' }}
                        />
                    </MenuItem>
                </Menu>
            )}
        </>
    );
};

export default EditablePriorityChip;
